import { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { getAnonId } from "@/lib/voteStore";
import { clearGuestVotes } from "@/lib/guestGate";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const api = axios.create({ baseURL: `${BACKEND_URL}/api`, withCredentials: true });

const AuthContext = createContext(null);

async function claimAnonVotes() {
  try { await api.post("/votes/claim", { anon_id: getAnonId() }); clearGuestVotes(); } catch (err) { /* non-fatal */ }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (window.location.hash?.includes('session_id=')) { setLoading(false); return; }
    checkAuth();
  }, [checkAuth]);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data);
    await claimAnonVotes();
    return data;
  }, []);

  const register = useCallback(async (email, password, name) => {
    const { data } = await api.post("/auth/register", { email, password, name, ref: localStorage.getItem("fv_ref") || null });
    setUser(data);
    await claimAnonVotes();
    return data;
  }, []);

  const googleLogin = useCallback(() => {
    const redirectUrl = window.location.origin + '/';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  }, []);

  const processGoogleSession = useCallback(async (sessionId) => {
    const { data } = await api.post("/auth/google-session", { session_id: sessionId, ref: localStorage.getItem("fv_ref") || null });
    setUser(data);
    await claimAnonVotes();
    return data;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      console.error("Logout request failed", err);
    }
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({ user, loading, login, register, googleLogin, processGoogleSession, logout, checkAuth, api }),
    [user, loading, login, register, googleLogin, processGoogleSession, logout, checkAuth]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
export { api };
